// src/hrms/alarms/alarmNotifications.ts
import { Capacitor } from "@capacitor/core";
import { isElectron, showDesktopNotification } from "../../utils/electron";
import type { AlarmItemKey } from "./alarmTypes";

/**
 * System notifications for alarms.
 *
 *  - Browser: posted through a service worker (public/alarm-sw.js) because
 *    only a worker's notifications carry action buttons and survive the tab
 *    that posted them. The worker cannot ring on its own: browsers give it no
 *    timer that outlives a few idle seconds, so the open tab decides WHEN to
 *    ring and the worker only shows the notification and routes the click back.
 *  - Desktop app: a native notification through the main process (the same
 *    `notification:show` IPC chat uses). Its click focuses the window and
 *    navigates to the alarm's deep link, which AlarmHost carries out.
 */

export const ALARM_SW_URL = `${process.env.PUBLIC_URL || ""}/alarm-sw.js`;
/**
 * The worker's own scope. Not "/": src/index.js registers a self-destructing
 * /sw.js there in production (and unregisters every worker in development),
 * which would replace or remove an alarm worker sharing that scope. A worker
 * shows notifications for the whole origin whatever its scope.
 */
export const ALARM_SW_SCOPE = `${process.env.PUBLIC_URL || ""}/alarm-sw/`;
export const ALARM_SW_MESSAGE = "teamlio-alarm";

/** Where a click on an alarm notification lands: the alarm's page, with the answer for AlarmHost to carry out. */
export function alarmNotificationRoute(data: Pick<AlarmNotificationData, "itemKey" | "occurrenceId" | "url">, action: AlarmNotificationAction = "show") {
  const params = new URLSearchParams({ alarmAction: action, alarmItem: data.itemKey, alarmOccurrence: data.occurrenceId });
  // URLSearchParams writes ":" and "@" encoded, which the desktop app's deep-link check requires.
  return `${data.url}?${params.toString()}`;
}

/** What a click on an alarm notification asks the app to do. */
export type AlarmNotificationAction = "show" | "complete" | "snooze";

export type AlarmNotificationData = {
  itemKey: AlarmItemKey;
  occurrenceId: string;
  ownerId: string;
  /** Where the worker opens the app when no TeamLio tab is left to answer. */
  url: string;
};

export type AlarmPermission = NotificationPermission | "unsupported";

/** Alarms need a real browser tab: the Capacitor Android shell has no service worker notifications. */
export function browserAlarmsAvailable() {
  return typeof window !== "undefined" && !Capacitor.isNativePlatform();
}

export function notificationPermission(): AlarmPermission {
  // The desktop app's notifications are native; the OS, not the page, decides whether they show.
  if (isElectron) return "granted";
  if (typeof window === "undefined" || !("Notification" in window) || !("serviceWorker" in navigator)) return "unsupported";
  return Notification.permission;
}

/**
 * Asks for notification permission. Safari only shows the prompt for a call
 * made inside a click handler, so call this first thing in one, before any await.
 */
export function requestNotificationPermission(): Promise<AlarmPermission> {
  if (notificationPermission() === "unsupported") return Promise.resolve("unsupported");
  try {
    return Promise.resolve(Notification.requestPermission()).then((result) => result ?? Notification.permission);
  } catch {
    return Promise.resolve(Notification.permission);
  }
}

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

export function registerAlarmWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!registration) {
    registration = isElectron || !browserAlarmsAvailable() || !("serviceWorker" in navigator)
      ? Promise.resolve(null)
      : Promise.resolve()
          .then(() => navigator.serviceWorker.register(ALARM_SW_URL, { scope: ALARM_SW_SCOPE }))
          // `serviceWorker.ready` only settles for a worker controlling this page, which one with its own scope never does.
          .then(whenActive)
          // No worker (blocked by the browser, private mode, an http origin): alarms still ring in the page.
          .catch(() => null);
  }
  return registration;
}

function whenActive(worker: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
  const pending = worker.installing ?? worker.waiting;
  if (worker.active || !pending) return Promise.resolve(worker);
  return new Promise((resolve, reject) => {
    pending.addEventListener("statechange", () => {
      if (pending.state === "activated") resolve(worker);
      else if (pending.state === "redundant") reject(new Error("alarm worker failed to install"));
    });
  });
}

/** Test seam. */
export function resetAlarmWorkerRegistration() {
  registration = null;
}

/** Safari (macOS) shows web notifications as banners with no buttons and ignores `requireInteraction`. */
export function isSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /safari/i.test(ua) && !/chrome|chromium|crios|edg|android/i.test(ua);
}

function occurrenceTag(itemKey: AlarmItemKey, occurrenceId: string) {
  return `alarm|${itemKey}|${occurrenceId}|`;
}

export type AlarmNotificationContent = {
  body: string;
  data: AlarmNotificationData;
  notificationId: string;
  title: string;
};

/**
 * Posts one ring. Each ring gets its own tag (so the browser alerts again on a
 * repeat, which a replaced same-tag notification does not do in every
 * browser); the occurrence's earlier rings are closed first so only one is
 * ever on screen.
 */
export async function showAlarmNotification(content: AlarmNotificationContent): Promise<boolean> {
  if (isElectron) {
    // Silent: the app is still running behind its tray icon and plays the alarm's own sound.
    // A native notification has no buttons here; its click opens the ringing alarm.
    await showDesktopNotification({ body: content.body, route: alarmNotificationRoute(content.data), silent: true, title: content.title });
    return true;
  }
  if (notificationPermission() !== "granted") return false;
  const worker = await registerAlarmWorker();
  if (!worker) return false;
  await closeAlarmNotifications(content.data.itemKey, content.data.occurrenceId, content.notificationId);
  const options: NotificationOptions & { actions?: { action: string; title: string }[]; renotify?: boolean } = {
    body: content.body,
    data: content.data,
    icon: `${process.env.PUBLIC_URL || ""}/logo192.png`,
    requireInteraction: true,
    tag: content.notificationId,
    actions: [
      { action: "complete", title: "Complete" },
      { action: "snooze", title: "Snooze" },
    ],
  };
  try {
    await worker.showNotification(content.title, options);
    return true;
  } catch {
    return false;
  }
}

/** Closes every notification an occurrence posted (all of them when `exceptId` is not given). */
export async function closeAlarmNotifications(itemKey: AlarmItemKey, occurrenceId: string, exceptId?: string) {
  const worker = await registerAlarmWorker();
  if (!worker || typeof worker.getNotifications !== "function") return;
  try {
    const prefix = occurrenceTag(itemKey, occurrenceId);
    const open = await worker.getNotifications();
    open.filter((notification) => notification.tag.startsWith(prefix) && notification.tag !== exceptId).forEach((notification) => notification.close());
  } catch {
    // Nothing to close, or the browser will not list them: the next ring replaces them anyway.
  }
}

/** Every alarm notification still on screen, e.g. for an alarm turned off or an item completed elsewhere. */
export async function closeAlarmNotificationsWhere(keep: (data: AlarmNotificationData) => boolean) {
  const worker = await registerAlarmWorker();
  if (!worker || typeof worker.getNotifications !== "function") return;
  try {
    const open = await worker.getNotifications();
    open.filter((notification) => notification.tag.startsWith("alarm|") && !keep(notification.data as AlarmNotificationData)).forEach((notification) => notification.close());
  } catch {
    // As above.
  }
}
